import type { Era } from '../types'
import { formatYear } from '../utils/format'

interface Props {
  eras: Era[]
  selectedEraId: string | null
  selectedYear: number | null
  onSelectEra: (eraId: string | null) => void
  onSelectYear: (year: number | null) => void
  onOpenHelp: () => void
}

// Linear year axis from -1500 to 1947
const AXIS_START = -1500
const AXIS_END   = 1947

function pct(year: number) {
  return ((year - AXIS_START) / (AXIS_END - AXIS_START)) * 100
}

// Strip the leading "The " article from titles for display — keeps the full
// HCIP title otherwise (e.g., "Age of Imperial Unity", "British Paramountcy
// and Indian Renaissance, Part 1"). Long titles wrap to 2 lines inside the
// uniformly-sized grid cell.
function chipLabel(_eraId: string, fullTitle: string): string {
  return fullTitle.replace(/^The /, '')
}

export default function EraRibbon({ eras, selectedEraId, selectedYear, onSelectEra, onSelectYear, onOpenHelp }: Props) {
  return (
    <section className="bg-parchment-200/40 border-b border-ink-300/10 relative z-10">
      <div className="max-w-[1800px] mx-auto px-6 py-3">
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="text-[10px] uppercase tracking-[0.18em] text-ink-50 font-body font-medium">Eras &amp; Years</h2>
          {(selectedYear !== null || selectedEraId) && (
            <button
              onClick={() => { onSelectEra(null); onSelectYear(null) }}
              className="text-xs text-saffron-400 hover:text-saffron-500"
            >Clear time</button>
          )}
        </div>

        {/* Era chips — justified grid. Equal-width columns (3 on mobile, 6 on
            lg+) and a fixed chip height (h-12) so every card is exactly the
            same size regardless of title length. Long titles wrap to a
            second line inside the chip. Slot 1 is the Bhūmikā reader's
            guide; the 11 era chips follow in slots 2–12. Year range + full
            title + IAST + blurb live in the title-tooltip. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-1.5 mb-3">
          {/* Slot 1 — Bhūmikā (Sanskrit for "preface / preamble"), the
              conventional name for the introductory matter of a Sanskrit
              work. Rendered FIRST so it lands top-left of the grid (was
              previously bottom-right). Same h-14 footprint as the era chips
              so the grid stays justified, but a distinct indigo_deep palette
              so it doesn't read as just another era. */}
          <button
            onClick={onOpenHelp}
            className="h-14 px-2.5 rounded font-display iast text-[15.5px] font-semibold leading-tight text-center flex items-center justify-center transition-colors bg-indigo_deep-50 text-indigo_deep-500 hover:bg-indigo_deep-100 hover:text-indigo_deep-400 border border-indigo_deep-200/40"
            title="Bhūmikā — a guide to reading the atlas"
          >
            Bhūmikā · Reader's Guide
          </button>
          {eras.map(era => {
            const active = era.id === selectedEraId
            const tooltip = `${era.title}${era.titleIAST ? ` · ${era.titleIAST}` : ''} (${formatYear(era.startYear)} – ${formatYear(era.endYear)})\n\n${era.blurb}`
            return (
              <button
                key={era.id}
                // Single call: onSelectEra() already clears year via App.tsx.
                // Calling onSelectYear(null) here used to also clear era as a
                // side effect, so era clicks were silently self-cancelling.
                onClick={() => onSelectEra(active ? null : era.id)}
                className={`h-14 px-2.5 rounded font-display iast text-[15.5px] font-semibold leading-tight text-center flex items-center justify-center transition-colors ${
                  active
                    ? 'bg-saffron-50 text-saffron-500 ring-1 ring-saffron-300'
                    : 'bg-parchment-50 text-ink-100 hover:bg-saffron-50/60 hover:text-saffron-400 border border-ink-300/5'
                }`}
                title={tooltip}
              >
                {chipLabel(era.id, era.title)}
              </button>
            )
          })}
        </div>

        {/* Year slider. Layered structure (bottom → top):
             1. visible track bar (so users can SEE where to grab)
             2. era bands (highlight which era contains a given year)
             3. native <input type=range> (transparent except for the thumb,
                styled in themes.css to be a clearly visible pill)
             4. selected-year readout above the thumb
             5. tick labels BELOW the slider area, pointer-events-none so
                clicking on them passes through to the input
            The container is h-14 (56px) to give the labels room to sit fully
            below the slider's hit zone — previously they overlapped the input
            and intercepted clicks. */}
        <div className="relative h-14 select-none pt-1">
          {/* Visible track — uses bg-ink-300/* so it auto-themes via the
              broadsheet override (dark on cream → dark on white). */}
          <div className="absolute inset-x-0 top-[18px] h-[14px] rounded-sm bg-ink-300/10 border border-ink-300/20 pointer-events-none" />

          {/* Era bands (sit inside the track so they read as colored regions
              of it). Pointer-events-none so clicks on a band still register
              on the input below. */}
          {eras.map(era => (
            <div
              key={era.id}
              className={`absolute top-[20px] h-[10px] rounded-sm pointer-events-none ${era.id === selectedEraId ? 'bg-saffron-100/80' : 'bg-parchment-300/50'}`}
              style={{
                left: `${pct(era.startYear)}%`,
                width: `${pct(era.endYear) - pct(era.startYear)}%`
              }}
              aria-hidden="true"
            />
          ))}

          {/* Slider input — transparent except for its thumb (themes.css). */}
          <input
            type="range"
            min={AXIS_START}
            max={AXIS_END}
            step={5}
            value={selectedYear ?? -322}
            onChange={e => onSelectYear(Number(e.target.value))}
            className="year-slider absolute inset-x-0 top-[14px] w-full h-[22px] z-10"
            aria-label="Select year"
          />

          {/* Selected-year readout — above the thumb. */}
          {selectedYear !== null && (
            <div
              className="absolute top-0 -translate-x-1/2 text-[11px] font-medium text-saffron-500 whitespace-nowrap pointer-events-none"
              style={{ left: `${pct(selectedYear)}%` }}
            >▼ {formatYear(selectedYear)}</div>
          )}

          {/* Tick labels — below the slider, never intercept clicks. On the
              smallest phones (<sm) the 8 labels crowd; hide the in-between
              ticks (-1000, 0, 1000) so we keep five clean anchors. */}
          {[-1500, -1000, -500, 0, 500, 1000, 1500, 1947].map(t => {
            const hideOnSmall = t === -1000 || t === 0 || t === 1000
            return (
              <div
                key={t}
                className={`absolute top-[42px] -translate-x-1/2 text-[9px] text-ink-50 pointer-events-none ${hideOnSmall ? 'hidden sm:block' : ''}`}
                style={{ left: `${pct(t)}%` }}
              >{t === 0 ? '0' : formatYear(t)}</div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
